import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";

const UMDepartmentForm = ({ initialData, campuses, faculties, onSubmit, onCancel, loading }) => {
  const { t } = useTranslation("universityManager");
  const { t: tErrors } = useTranslation("errors");
  const { can } = useUniAdminAuth();
  const canListCampuses = can(PERMISSIONS.campus.list);
  const canListFaculties = can(PERMISSIONS.faculty.list);

  const [formData, setFormData] = useState({
    campusId: "",
    facultyId: "",
    nameEn: "",
    nameLocal: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        campusId: initialData.campusId || "",
        facultyId: initialData.facultyId || "",
        nameEn: initialData.nameEn || "",
        nameLocal: initialData.nameLocal || "",
      });
    } else {
      setFormData({ campusId: "", facultyId: "", nameEn: "", nameLocal: "" });
    }
    setErrors({});
  }, [initialData]);

  const filteredFaculties = useMemo(() => {
    if (!formData.campusId) return faculties;
    return faculties.filter((f) => String(f.campusId) === String(formData.campusId));
  }, [faculties, formData.campusId]);

  const validate = () => {
    const newErrors = {};
    if (!formData.campusId) newErrors.campusId = t("crud.errors.campusRequired");
    if (!formData.facultyId) newErrors.facultyId = t("crud.errors.facultyRequired");
    if (!formData.nameEn.trim()) newErrors.nameEn = t("crud.errors.nameEnRequired");
    if (!formData.nameLocal.trim()) newErrors.nameLocal = t("crud.errors.nameLocalRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate() || loading) return;
    onSubmit({
      facultyId: formData.facultyId,
      nameEn: formData.nameEn.trim(),
      nameLocal: formData.nameLocal.trim(),
    });
  };

  const handleChange = (field, value) => {
    if (field === "campusId") {
      setFormData((prev) => ({ ...prev, campusId: value, facultyId: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* Campus & Faculty Selection */}
      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="form-label fw-medium">
            {t("crud.fields.campus")} <span className="text-danger">*</span>
          </label>
          <select
            className={`form-select ${errors.campusId ? "is-invalid" : ""}`}
            value={formData.campusId}
            onChange={(e) => handleChange("campusId", e.target.value)}
            disabled={loading || !canListCampuses}
            required
          >
            <option value="">{t("crud.selectCampus")}</option>
            {campuses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nameEn}
              </option>
            ))}
          </select>
          {errors.campusId && <div className="invalid-feedback">{errors.campusId}</div>}
          {!canListCampuses && <small className="text-danger">{tErrors("permission.denied")}</small>}
        </div>
        <div className="col-md-6">
          <label className="form-label fw-medium">
            {t("crud.fields.faculty")} <span className="text-danger">*</span>
          </label>
          <select
            className={`form-select ${errors.facultyId ? "is-invalid" : ""}`}
            value={formData.facultyId}
            onChange={(e) => handleChange("facultyId", e.target.value)}
            disabled={loading || !canListFaculties || !formData.campusId}
            required
          >
            <option value="">
              {!formData.campusId ? t("crud.selectCampusFirst") : t("crud.selectFaculty")}
            </option>
            {filteredFaculties.map((f) => (
              <option key={f.id} value={f.id}>
                {f.nameEn}
              </option>
            ))}
          </select>
          {errors.facultyId && <div className="invalid-feedback">{errors.facultyId}</div>}
          {!canListFaculties ? (
            <small className="text-danger">{tErrors("permission.denied")}</small>
          ) : !formData.campusId ? (
            <small className="text-muted">{t("crud.selectCampusFirst")}</small>
          ) : null}
        </div>
      </div>

      {/* Department Names */}
      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="form-label fw-medium">
            {t("crud.fields.nameEn")} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${errors.nameEn ? "is-invalid" : ""}`}
            value={formData.nameEn}
            onChange={(e) => handleChange("nameEn", e.target.value)}
            placeholder={t("crud.placeholders.nameEn")}
            disabled={loading}
          />
          {errors.nameEn && <div className="invalid-feedback">{errors.nameEn}</div>}
        </div>
        <div className="col-md-6">
          <label className="form-label fw-medium">
            {t("crud.fields.nameLocal")} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${errors.nameLocal ? "is-invalid" : ""}`}
            value={formData.nameLocal}
            onChange={(e) => handleChange("nameLocal", e.target.value)}
            placeholder={t("crud.placeholders.nameLocal")}
            disabled={loading}
          />
          {errors.nameLocal && <div className="invalid-feedback">{errors.nameLocal}</div>}
        </div>
      </div>

      {/* Read-only Info */}
      {initialData && (
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label fw-medium">{t("crud.fields.faculty")}</label>
            <input
              type="text"
              className="form-control"
              value={initialData.facultyName || ""}
              readOnly
              disabled
            />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-medium">{t("crud.fields.studentCount")}</label>
            <input
              type="number"
              className="form-control"
              value={initialData.studentCount ?? 0}
              readOnly
              disabled
            />
          </div>
        </div>
      )}

      {/* Form Actions */}
      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        {onCancel && (
          <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={loading}>
            <i className="fas fa-times me-2"></i>
            {t("crud.cancel")}
          </button>
        )}
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              {t("crud.saving")}
            </>
          ) : (
            <>
              <i className={`fas ${initialData ? "fa-save" : "fa-plus"} me-2`}></i>
              {initialData ? t("crud.save") : t("crud.addDepartment")}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default UMDepartmentForm;
